// RealtimeStore: holds RealtimeService instance and wires events to consumers
import { makeAutoObservable, runInAction } from "mobx";
import { DEFAULT_REALTIME_OUTPUT, RealtimeService, REALTIME_STATES, normalizeRealtimeOutputConfig } from "../services/realtime.service";
import { RealtimeRelay } from "../services/realtimeRelay.service";

export class RealtimeStore {
  constructor() {
    this.service = new RealtimeService();
    this.connectionState = REALTIME_STATES.STOPPED;
    this.captions = "";
    this.part = 0;
    this.section = null;
    this.ready = false;
    this.outputVoice = DEFAULT_REALTIME_OUTPUT.voice;
    this.outputSpeed = DEFAULT_REALTIME_OUTPUT.speed;
    this.reconnectCount = 0;
    this._transcriptBuffer = ""; // buffer for current section/part
    this.assistantSpeaking = false; // true while output_audio_buffer is active
    makeAutoObservable(this, {}, { autoBind: true });

    this.service.on("state", (s) => { runInAction(() => { this.connectionState = s; }); });
    this.service.on("ready", (info) => {
      // info is passed directly as the first argument from emitEvent("ready", [{ isReconnect }])
      try {
        console.log("[RealtimeStore] ready-raw", info);
      } catch (e) { void e; }
      const normalized = info && typeof info === "object" ? info : { isReconnect: false };
      runInAction(() => {
        const prevReconnect = this.reconnectCount;
        this.ready = true;
        this.isReconnect = !!normalized.isReconnect;
        try {
          console.log("[RealtimeStore] ready", { info: normalized, isReconnect: this.isReconnect, prevReconnectCount: prevReconnect });
        } catch (e) { void e; }
        if (this.isReconnect) {
          this.reconnectCount = prevReconnect + 1;
          try {
            console.log("[RealtimeStore] reconnectCountIncrement", { reconnectCount: this.reconnectCount });
          } catch (e) { void e; }
        }
      });
    });
    this.service.on("captions", (text) => { runInAction(() => { this.captions = text; }); });
    this.service.on("outputStarted", () => { runInAction(() => { this.assistantSpeaking = true; }); });
    this.service.on("outputStopped", () => { 
      runInAction(() => { 
        this.assistantSpeaking = false; 
      }); 
    });
    // Deltas are used only for live captions; do not add them to the transcript buffer
    this.service.on("transcriptDelta", ({ delta }) => { void delta; });
    // Service already prefixes and formats interviewer lines, including newlines
    this.service.on("transcriptDone", ({ text }) => { if (text) this._appendDelta(text); });
    this.service.on("inputTranscriptionCompleted", ({ text }) => { if (text) this._appendDelta("Candidate: " + text + "\n"); });
    this.service.on("functionCall", ({ name, args }) => { this._handleFunctionCall(name, args); });
    this._onSectionAdvance = null; // callback injected by InterviewStore for section rotation
    this._onInterviewFinished = null; // callback for finalization
  }

  get outputConfig() {
    return {
      voice: this.outputVoice,
      speed: this.outputSpeed,
    };
  }

  setOutputConfig(output = {}) {
    const next = normalizeRealtimeOutputConfig({
      ...this.outputConfig,
      ...output,
    });
    this.outputVoice = next.voice;
    this.outputSpeed = next.speed;
    this.service.setOutputConfig(next);
  }

  async connect({ key, inputStream, audioEl, part, startTimeMs, tools, section, sectionStartTimeMs, applicationId }) {
    this.part = part || 0;
    this.section = section || this.section;
    await this.service.connect({
      key,
      inputStream,
      audioEl,
      part,
      startTimeMs,
      tools,
      output: this.outputConfig,
      section: this.section,
      sectionStartTimeMs,
      applicationId,
    });
  }

  /**
   * Reconnect with a fresh key/section, preserving media.
   */
  async reconnect({ key, section, sectionStartTimeMs, applicationId }) {
    this.section = section || this.section;
    // Teardown doesn't emit outputStopped (emitting domain events there could re-trigger
    // rotation deferral), so clear this flag directly — it must not stay stale-true across
    // a section change.
    runInAction(() => { this.assistantSpeaking = false; });
    await this.service.reconnect({ key, section: this.section, sectionStartTimeMs, applicationId });
  }

  sendInitialMessage() { this.service.sendInitialMessage(); }
  sendReconnectMessage(args) { this.service.sendReconnectMessage(args); }
  sendFinishRequest(args) { this.service.sendFinishRequest(args); }
  requestResponse() { this.service.requestResponse(); }

  /**
   * Wait for any in-flight candidate transcription to land before a section flush/teardown.
   */
  waitForInputTranscriptionsSettled(opts) { return this.service.waitForInputTranscriptionsSettled(opts); }

  // Dev/test only: send a deliberately malformed response.create to Azure to exercise the
  // transient-error retry path. persistent:false -> model recovers; persistent:true -> retries exhaust.
  simulateAzureError({ persistent = false } = {}) { return this.service._devSimulateAzureError({ persistent }); }

  teardown() { this.service.teardown(); runInAction(() => { this.ready = false; }); }

  /**
   * Dev-only: test ONLY the relay WebSocket connection (endpoint reachability + JWT auth),
   * independent of the WebRTC/Azure setup. Opens a throwaway relay, resolves with
   * { ok, detail } on first signal (open / error / timeout), then tears it down.
   */
  async testRelay({ timeoutMs = 6000 } = {}) {
    const log = (tag, payload) => { try { this.service._log(`RelayTest:${tag}`, payload); } catch { /* noop */ } };
    log("start", { note: "relay connection only" });
    return new Promise((resolve) => {
      let settled = false;
      let timer = null;
      let relay = null;
      const finish = (result) => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        log("result", result);
        try { relay?.stop(); } catch { /* noop */ }
        resolve(result);
      };
      relay = new RealtimeRelay({
        onOpen: () => finish({ ok: true, detail: "Relay connected (endpoint + auth OK)" }),
        onError: (d) => finish({ ok: false, detail: d?.message || "relay error" }),
        onLog: (tag, payload) => log(tag, payload),
      });
      timer = setTimeout(() => finish({ ok: false, detail: `No relay response within ${Math.round(timeoutMs / 1000)}s` }), timeoutMs);
      relay.start({
        applicationId: this.service?._applicationId || null,
        sectionId: this.section?._id || this.section?.id || null,
        callId: `relay-test-${Math.random().toString(36).slice(2, 10)}`,
      });
    });
  }

  get transcriptBuffer() { return this._transcriptBuffer; }

  _appendDelta(text) { this._transcriptBuffer += text; }

  /**
   * Flush and return buffered transcript content (used by InterviewStore). Resets buffer.
   */
  consumeTranscriptBuffer() {
    const out = this._transcriptBuffer;
    this._transcriptBuffer = "";
    return out;
  }

  _handleFunctionCall(name, rawArgs) {
    // Basic dispatch; actual wiring (e.g., InterviewStore callbacks) will be injected later.
    this.lastFunctionCall = { name, rawArgs };
    // Consumers can observe lastFunctionCall or we can emit an event in future.
    if (name === "part_finished" && this._onSectionAdvance) {
      // Rotation store now handles all timing boundaries and deferred rotation logic
      // Just call the callback immediately
      this._onSectionAdvance();
    }
    if (name === "interview_finished" && this._onInterviewFinished) {
      let parsed = null;
      try { parsed = rawArgs && JSON.parse(rawArgs); } catch (err) { void err; parsed = null; }
      this._onInterviewFinished(parsed || {});
    }
  }
}
